import { useState } from 'react';
import { Check, Package } from 'lucide-react';
import FilterService from '../../services/filterService';

function ExpenseGroupFilter({ selectedGroups = [], selectedDepartments = [], onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  // Get available expense groups based on selected departments
  const availableGroups = FilterService.getAvailableExpenseGroups(selectedDepartments);
  
  const expenseGroups = [
    {
      id: 'Inventory',
      name: 'Inventory',
      description: 'Physical items and equipment',
      color: '#3b82f6',
      examples: ['Hardware', 'Equipment', 'Vehicles']
    },
    {
      id: 'Non-Inventory',
      name: 'Non-Inventory',
      description: 'Services and consumables',
      color: '#10b981',
      examples: ['Software licenses', 'Services', 'Utilities']
    }
  ].filter(group => availableGroups.includes(group.id));

  const handleGroupToggle = (groupId) => {
    const newSelection = selectedGroups.includes(groupId)
      ? selectedGroups.filter(id => id !== groupId)
      : [...selectedGroups, groupId];
    
    onChange(newSelection);
  };

  const selectAll = () => {
    onChange(expenseGroups.map(group => group.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getSelectedGroupNames = () => {
    return expenseGroups
      .filter(group => selectedGroups.includes(group.id))
      .map(group => group.name);
  };

  const selectedNames = getSelectedGroupNames();
  const hasSelection = selectedGroups.length > 0;

  // Show disabled state if no departments selected
  const isDisabled = selectedDepartments.length === 0;

  return (
    <div className="relative">
      {/* Group Selection Display */}
      <div 
        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        style={{ 
          background: hasSelection ? 'rgba(139, 69, 19, 0.05)' : 'var(--bg)',
          borderColor: hasSelection ? 'var(--primary)' : 'var(--border)'
        }}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          <span className="text-sm" style={{ color: 'var(--text)' }}>
            {isDisabled 
              ? 'Select departments first'
              : hasSelection 
                ? selectedNames.join(', ')
                : 'Select expense groups'
            }
          </span>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !isDisabled && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 p-2 rounded-lg border shadow-lg z-10"
          style={{ 
            background: 'var(--panel)',
            borderColor: 'var(--border)'
          }}
        >
          {/* Header Actions */}
          <div className="flex justify-between items-center p-2 border-b mb-2" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
              Expense Groups
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs px-2 py-1 rounded"
                style={{ 
                  background: 'var(--primary)',
                  color: 'white'
                }}
              >
                All
              </button>
              <button
                onClick={clearAll}
                className="text-xs px-2 py-1 rounded border"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Group Options */}
          <div className="space-y-2">
            {expenseGroups.map((group) => {
              const isSelected = selectedGroups.includes(group.id);
              
              return (
                <div
                  key={group.id}
                  className="p-3 rounded-md cursor-pointer border transition-colors"
                  style={{ 
                    background: isSelected ? 'rgba(139, 69, 19, 0.1)' : 'var(--bg)',
                    borderColor: isSelected ? group.color : 'var(--border)'
                  }}
                  onClick={() => handleGroupToggle(group.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div 
                      className="w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5"
                      style={{ 
                        borderColor: isSelected ? group.color : 'var(--border)',
                        background: isSelected ? group.color : 'transparent'
                      }}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    {/* Group Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ background: group.color }}
                        />
                        <span className="font-medium text-sm" style={{ color: 'var(--text)' }}>
                          {group.name}
                        </span>
                      </div>
                      
                      <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>
                        {group.description}
                      </p>
                      
                      <div className="text-xs" style={{ color: 'var(--muted)' }}>
                        <span className="font-medium">Examples: </span>
                        {group.examples.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No Options Available */}
          {expenseGroups.length === 0 && (
            <div className="text-center py-4">
              <Package className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--muted)' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                No expense groups available for selected departments
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-3 pt-2 border-t text-center" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => setIsOpen(false)}
              className="text-xs px-3 py-1 rounded"
              style={{ 
                background: 'var(--border)',
                color: 'var(--text)'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseGroupFilter;